// Page4.js - Roof Material Selection & Contact Form
'use strict';

import { logger, FIELDS } from '../utils/logger.js';

/**
 * Page4: Roof Material Selection & Contact Form
 * Allows users to select current and new roof materials, then provide contact information
 */
class Page4 {
  constructor(widget) {
    this.widget = widget;
    this.shadowRoot = widget.shadowRoot;
    this.config = widget.config;

    // Form state
    this.currentMaterial = null;
    this.newMaterial = null;
    this.contactInfo = {
      firstName: '',
      lastName: '',
      phone: '',
      email: ''
    };

    // Step management
    this.currentStep = 1;
  }

  render() {
    return `
      <!-- Page 4: Roof Material Selection & Contact Form -->
      <div class="page" id="page-4" style="display: block;">
        <div class="page-content">
          <!-- Page Header -->
          <div class="page-header">
            <h2 class="page-title">Get Your Roof Quote</h2>
            <p class="page-subtitle">Select your roof materials and provide contact information</p>
          </div>

          <!-- Selected Roof Summary -->
          <div class="roof-summary" id="roof-summary">
            <!-- Will be populated with selected roof faces data -->
          </div>

          <!-- Step 1: Current Roof Material Selection -->
          <div class="step-section" id="step-1" style="display: block;">
            <div class="step-header">
              <h3 class="section-title">
                <span class="step-number">1</span>
                Select Your Current Roof Material
              </h3>
            </div>
            <div class="material-grid" id="current-material-grid">
              ${this.generateMaterialCards('current')}
            </div>
          </div>

          <!-- Step 2: New Roof Material Selection -->
          <div class="step-section" id="step-2" style="display: none;">
            <div class="nav-controls">
              <button class="back-btn" id="back-to-step-1">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                Back
              </button>
            </div>
            <div class="step-header">
              <h3 class="section-title">
                <span class="step-number">2</span>
                Select Your New Roof Material
              </h3>
            </div>
            <div class="material-grid" id="new-material-grid">
              ${this.generateMaterialCards('new')}
            </div>
          </div>

          <!-- Step 3: Contact Information Form -->
          <div class="step-section" id="step-3" style="display: none;">
            <div class="nav-controls">
              <button class="back-btn" id="back-to-step-2">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                Back
              </button>
            </div>
            <div class="step-header">
              <h3 class="section-title">
                <span class="step-number">3</span>
                Contact Information
              </h3>
            </div>
            <form class="contact-form" id="contact-form">
              <div class="form-row">
                <div class="form-group">
                  <label for="first-name">First Name</label>
                  <input type="text" id="first-name" name="firstName" required>
                </div>
                <div class="form-group">
                  <label for="last-name">Last Name</label>
                  <input type="text" id="last-name" name="lastName" required>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="phone">Phone Number</label>
                  <input type="tel" id="phone" name="phone" required>
                </div>
                <div class="form-group">
                  <label for="email">Email Address</label>
                  <input type="email" id="email" name="email" required>
                </div>
              </div>
            </form>

            <!-- Submit Button (only shown on step 3) -->
            <div class="submit-section">
              <button class="submit-btn" id="submit-quote-btn" disabled>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                </svg>
                <span>Request Your Quote</span>
              </button>
            </div>
          </div>
        </div>

        <style>
          /* Page 4 Styles */
          #page-4 {
            max-height: calc(90vh - 80px);
            overflow-y: auto;
            overflow-x: hidden;
            display: flex;
            flex-direction: column;
          }

          .page-content {
            flex: 1;
            padding: 20px;
            padding-bottom: 100px; /* Space for submit button */
          }

          .page-header {
            text-align: center;
            margin-bottom: 32px;
          }

          .page-title {
            font-size: 24px;
            font-weight: 700;
            color: #000000;
            margin: 0 0 8px 0;
          }

          .page-subtitle {
            font-size: 14px;
            color: #000000;
            margin: 0;
          }

          /* Roof Summary */
          .roof-summary {
            background: #f9fafb;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 32px;
            border: 1px solid #e5e7eb;
          }

          .summary-title {
            font-size: 16px;
            font-weight: 600;
            color: #000000;
            margin: 0 0 12px 0;
          }

          .summary-stats {
            display: flex;
            gap: 24px;
            flex-wrap: wrap;
          }

          .summary-stat {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .stat-value {
            font-size: 20px;
            font-weight: 700;
            color: #22c55e;
          }

          .stat-label {
            font-size: 12px;
            color: #000000;
          }

          /* Step Sections */
          .step-section {
            margin-bottom: 32px;
          }

          .step-header {
            margin-bottom: 24px;
          }

          .nav-controls {
            margin-bottom: 16px;
          }

          .back-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background: white;
            border: 1px solid #cbd5e1;
            border-radius: 8px;
            color: #000000;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .back-btn:hover {
            background: #f1f5f9;
            border-color: #94a3b8;
            transform: translateX(-2px);
          }

          .back-btn svg {
            width: 16px;
            height: 16px;
          }

          .section-title {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 18px;
            font-weight: 600;
            color: #000000;
            margin: 0 0 20px 0;
          }

          .step-number {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
            color: white;
            border-radius: 50%;
            font-size: 14px;
            font-weight: 700;
          }

          /* Material Grid */
          .material-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 16px;
          }

          .material-card {
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 12px;
            padding: 20px;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
          }

          .material-card:hover {
            border-color: #cbd5e1;
            transform: translateY(-2px);
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          }

          .material-card.selected {
            border-color: #6366f1;
            background: rgba(99, 102, 241, 0.05);
            box-shadow: 0 0 0 1px #6366f1;
          }

          .material-icon {
            width: 48px;
            height: 48px;
            margin: 0 auto 12px;
            background: #f3f4f6;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
          }

          .material-name {
            font-size: 16px;
            font-weight: 600;
            color: #000000;
            margin-bottom: 8px;
          }

          .material-description {
            font-size: 12px;
            color: #000000;
            line-height: 1.4;
          }

          /* Contact Form */
          .contact-form {
            background: #f9fafb;
            border-radius: 12px;
            padding: 24px;
            border: 1px solid #e5e7eb;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 20px;
          }

          .form-group {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .form-group label {
            font-size: 14px;
            font-weight: 500;
            color: #000000;
          }

          .form-group input {
            padding: 12px 16px;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            background: white;
            color: #000000;
            font-size: 14px;
            transition: all 0.3s ease;
          }

          .form-group input:focus {
            outline: none;
            border-color: #6366f1;
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          }

          .form-group input::placeholder {
            color: #9ca3af;
          }

          /* Submit Section */
          .submit-section {
            position: sticky;
            bottom: 0;
            background: linear-gradient(to top, #ffffff 0%, #ffffff 70%, transparent 100%);
            padding: 20px 0;
            margin: 0 -20px;
            text-align: center;
          }

          .submit-btn {
            padding: 16px 32px;
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
            color: white;
            font-size: 16px;
            font-weight: 600;
            border: none;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 auto;
            position: relative;
            overflow: hidden;
          }

          .submit-btn::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
            transition: left 0.3s ease;
            z-index: 0;
          }

          .submit-btn span,
          .submit-btn svg {
            position: relative;
            z-index: 1;
          }

          .submit-btn:hover:not(:disabled)::before {
            left: 0;
          }

          .submit-btn:hover:not(:disabled) {
            box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
          }

          .submit-btn:disabled {
            background: #374151;
            cursor: not-allowed;
            box-shadow: none;
          }

          .submit-btn:disabled::before {
            display: none;
          }

          .submit-btn svg {
            width: 20px;
            height: 20px;
          }

          /* Responsive */
          @media (max-width: 768px) {
            .form-row {
              grid-template-columns: 1fr;
            }

            .material-grid {
              grid-template-columns: 1fr;
            }

            .summary-stats {
              flex-direction: column;
              gap: 12px;
            }

            .section-title {
              justify-content: center;
            }
          }
        </style>
      </div>
    `;
  }

  generateMaterialCards(type) {
    const materials = this.getMaterialOptions();

    return materials.map(material => `
      <div class="material-card" data-material-type="${type}" data-material-id="${material.id}">
        <div class="material-icon">${material.icon}</div>
        <div class="material-name">${material.name}</div>
        <div class="material-description">${material.description}</div>
      </div>
    `).join('');
  }

  getMaterialOptions() {
    return [
      {
        id: 'asphalt-shingles',
        name: 'Asphalt Shingles',
        icon: '🏠',
        description: 'Most common residential roofing material'
      },
      {
        id: 'metal-roofing',
        name: 'Metal Roofing',
        icon: '🔧',
        description: 'Durable steel, aluminum, or copper panels'
      },
      {
        id: 'tile-roofing',
        name: 'Tile Roofing',
        icon: '🧱',
        description: 'Clay or concrete tiles, Mediterranean style'
      },
      {
        id: 'slate-roofing',
        name: 'Slate Roofing',
        icon: '🪨',
        description: 'Natural stone, premium luxury material'
      },
      {
        id: 'wood-shingles',
        name: 'Wood Shingles',
        icon: '🌲',
        description: 'Cedar or redwood, natural appearance'
      },
      {
        id: 'flat-membrane',
        name: 'Flat/Membrane',
        icon: '📋',
        description: 'EPDM, TPO, or modified bitumen'
      }
    ];
  }

  /**
   * Called when page is mounted/shown
   */
  onMount(data = {}) {
    // Reset to step 1
    this.currentStep = 1;
    this.goToStep(1);

    // Get selected roof faces data from session
    const selectedFaces = this.widget.getSessionData('selected_roof_faces');
    if (selectedFaces) {
      this.displayRoofSummary(selectedFaces);
    }

    // Setup event handlers
    this.setupEventHandlers();
  }

  displayRoofSummary(selectedFacesData) {
    const summaryContainer = this.shadowRoot.querySelector('#roof-summary');
    if (!summaryContainer || !selectedFacesData.faces) return;

    const faces = selectedFacesData.faces;
    const totalArea = selectedFacesData.totalArea || 0;

    summaryContainer.innerHTML = `
      <div class="summary-title">Selected Roof Analysis</div>
      <div class="summary-stats">
        <div class="summary-stat">
          <div class="stat-value">${faces.length}</div>
          <div class="stat-label">Roof Sections</div>
        </div>
        <div class="summary-stat">
          <div class="stat-value">${totalArea.toFixed(1)} m²</div>
          <div class="stat-label">Total Area</div>
        </div>
        <div class="summary-stat">
          <div class="stat-value">${(totalArea * 10.764).toFixed(0)} ft²</div>
          <div class="stat-label">Total Area (sq ft)</div>
        </div>
      </div>
    `;
  }

  setupEventHandlers() {
    // Material card selection handlers
    const materialCards = this.shadowRoot.querySelectorAll('.material-card');
    materialCards.forEach(card => {
      card.addEventListener('click', () => this.handleMaterialSelection(card));
    });

    // Form input handlers
    const formInputs = this.shadowRoot.querySelectorAll('#contact-form input');
    formInputs.forEach(input => {
      input.addEventListener('input', () => this.handleFormInput(input));
    });

    // Submit button handler
    const submitBtn = this.shadowRoot.querySelector('#submit-quote-btn');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => this.handleSubmit());
    }

    // Back button handlers
    const backToStep1 = this.shadowRoot.querySelector('#back-to-step-1');
    if (backToStep1) {
      backToStep1.addEventListener('click', () => this.goToStep(1));
    }

    const backToStep2 = this.shadowRoot.querySelector('#back-to-step-2');
    if (backToStep2) {
      backToStep2.addEventListener('click', () => this.goToStep(2));
    }
  }

  handleMaterialSelection(card) {
    const materialType = card.dataset.materialType; // 'current' or 'new'
    const materialId = card.dataset.materialId;

    // Remove previous selection in this category
    const typeCards = this.shadowRoot.querySelectorAll(`[data-material-type="${materialType}"]`);
    typeCards.forEach(c => c.classList.remove('selected'));

    // Select this card
    card.classList.add('selected');

    // Update state
    if (materialType === 'current') {
      this.currentMaterial = materialId;

      // Auto-advance to step 2 after selecting current material
      setTimeout(() => this.goToStep(2), 300);

    } else if (materialType === 'new') {
      this.newMaterial = materialId;

      // Auto-advance to step 3 after selecting new material
      setTimeout(() => this.goToStep(3), 300);
    }

    this.updateSubmitButton();
  }

  goToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > 3) {
      logger.error({
        [FIELDS.OPERATION]: 'goToStep',
        [FIELDS.PAGE_NUMBER]: 4,
        [FIELDS.REASON]: 'invalid_step_number',
        stepNumber
      }, 'Invalid step number');
      return;
    }

    // Hide all steps
    for (let i = 1; i <= 3; i++) {
      const stepElement = this.shadowRoot.querySelector(`#step-${i}`);
      if (stepElement) {
        stepElement.style.display = 'none';
      }
    }

    // Show target step
    const targetStep = this.shadowRoot.querySelector(`#step-${stepNumber}`);
    if (targetStep) {
      targetStep.style.display = 'block';
      this.currentStep = stepNumber;

      // Add a gentle fade-in animation
      targetStep.style.opacity = '0';
      setTimeout(() => {
        targetStep.style.transition = 'opacity 0.3s ease';
        targetStep.style.opacity = '1';
      }, 50);
    }
  }

  handleFormInput(input) {
    const fieldName = input.name;
    const value = input.value.trim();

    this.contactInfo[fieldName] = value;

    this.updateSubmitButton();
  }

  updateSubmitButton() {
    const submitBtn = this.shadowRoot.querySelector('#submit-quote-btn');
    if (!submitBtn) return;

    // Check if all required fields are filled
    const hasCurrentMaterial = !!this.currentMaterial;
    const hasNewMaterial = !!this.newMaterial;
    const hasAllContactInfo = this.contactInfo.firstName &&
                             this.contactInfo.lastName &&
                             this.contactInfo.phone &&
                             this.contactInfo.email;

    const isComplete = hasCurrentMaterial && hasNewMaterial && hasAllContactInfo;

    submitBtn.disabled = !isComplete;
  }

  handleSubmit() {
    if (!this.currentMaterial || !this.newMaterial) {
      logger.error({
        [FIELDS.OPERATION]: 'handleSubmit',
        [FIELDS.PAGE_NUMBER]: 4,
        [FIELDS.REASON]: 'missing_material_selections',
        currentMaterial: this.currentMaterial,
        newMaterial: this.newMaterial
      }, 'Cannot submit - missing material selections');
      return;
    }

    if (!this.contactInfo.firstName || !this.contactInfo.lastName ||
        !this.contactInfo.phone || !this.contactInfo.email) {
      logger.error({
        [FIELDS.OPERATION]: 'handleSubmit',
        [FIELDS.PAGE_NUMBER]: 4,
        [FIELDS.REASON]: 'missing_contact_information',
        contactInfo: {
          hasFirstName: !!this.contactInfo.firstName,
          hasLastName: !!this.contactInfo.lastName,
          hasPhone: !!this.contactInfo.phone,
          hasEmail: !!this.contactInfo.email
        }
      }, 'Cannot submit - missing contact information');
      return;
    }

    // Get selected roof faces from session
    const selectedFaces = this.widget.getSessionData('selected_roof_faces');

    // Compile complete quote request data
    const quoteRequest = {
      roofData: selectedFaces,
      materials: {
        current: this.currentMaterial,
        new: this.newMaterial
      },
      contact: { ...this.contactInfo },
      timestamp: new Date().toISOString()
    };

    // Store in session for potential future use
    this.widget.setSessionData('quote_request', quoteRequest);

    // Navigate to Page5 (Quote Submission Status) with the quote data
    this.widget.showPage(5, { quoteData: quoteRequest });
  }


  /**
   * Called when page is unmounted/hidden
   */
  onUnmount() {
    // Cleanup if needed
  }
}

export default Page4;